// main.tsx
import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
// CAMBIO 1: Reemplazamos BrowserRouter por el *data router*.
//           Importamos createBrowserRouter, RouterProvider y useMatches.
import { createBrowserRouter, RouterProvider, useLocation } from 'react-router-dom';
import { App as AntdApp, ConfigProvider } from 'antd';
import { QueryClientProvider } from '@tanstack/react-query';

// Redux + Persist
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from '@store/store.tsx';
import { checkSession } from '@store/slices/authSlice.tsx';
import { loadInitialCart } from '@store/slices/cartSlice.tsx';

// Estilos globales
import 'bootstrap/dist/css/bootstrap.min.css';
import './index.css';
import '@styles/dashboard.css';
// import '@styles/CustomizarAntsDesign.css';

// SPA principal / rutas existentes de la app
import App from './App.tsx';
import RequireAuth from '@cfg/RequireAuth';
import { isPublicPath } from '@cfg/publicRoutes';
import { componentMapping, ComponentOrMapping, ComponentPage, isMappingNode } from '@cfg/pageRoutes.tsx';
import { logInfo } from '@tools/LogsHandler.tsx';
import { queryClient } from '@tools/queryClient.ts';
import { ThemeModeProvider, useThemeMode } from '@theme/hooks/useThemeMode.tsx';
import { runInChunks, scheduleIdle } from '@tools/perf/runInChunks.ts';
import BootLoadingScreen from '@components/BootLoadingScreen';
import { bootLog } from '@tools/bootLog';
// import { installAntdFloatingOverlayCoordinator } from '@tools/ui/antdFloatingOverlayCoordinator';
//
// installAntdFloatingOverlayCoordinator();

bootLog('store and persistor imported');


// ---------- Reutilizable: validar si un pathname es público ----------

// ---------- Wrapper de arranque: lee la URL real del navegador ----------
const isBootPublicPath = () => {
  const href = window.location?.href || '';
  let raw = window.location?.pathname || '/';
  try {
    raw = href ? new URL(href).pathname : raw;
  } catch {
    /* noop */
  }
  logInfo('isBootPublicPath ----- raw: ', raw, 'isPublicPath(raw): ', isPublicPath(raw))
  return isPublicPath(raw);
};

// ================================
// Componente que decide si envuelve App en RequireAuth
// ================================
const GuardedApp: React.FC = () => {
  const location = useLocation();
  const currentPath = location.pathname; // ← usar SIEMPRE el pathname real
  const isPublic = isPublicPath(currentPath);

  // ** Renderiza **
  return isPublic ? <App /> : <RequireAuth><App /></RequireAuth>;
};

const ThemeProviderBridge: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { themeConfig } = useThemeMode();
  return (
    <ConfigProvider theme={themeConfig}>
      <AntdApp>
        <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
      </AntdApp>
    </ConfigProvider>
  );
};

const Providers: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ThemeModeProvider>
    <ThemeProviderBridge>{children}</ThemeProviderBridge>
  </ThemeModeProvider>
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isComponentPage(v: any): v is ComponentPage {
  return v && typeof v.component === 'function';
}

// -----------------------------------------------------------------------------
// 1) 📌 Eliminamos completamente extractDataAttributes (obsoleto).
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// 2) 📌 Montaje dinámico de componentes sueltos (se mantiene BrowserRouter aquí).
//    Nota: Estos "islands" no usan `useMatches`, así que conservar BrowserRouter
//    evita cambios mayores y posibles conflictos con múltiples data routers.
// -----------------------------------------------------------------------------
type MountTask = { selector: string; Component: React.ElementType };

const collectMountTasks = (map: ComponentOrMapping, path: string[] = [], acc: MountTask[] = []) => {
  Object.entries(map).forEach(([key, val]) => {
    if (isComponentPage(val)) {
      acc.push({ selector: `[data-path="${path.concat(key).join('/')}"]`, Component: val.component });
    } else if (isMappingNode(val)) {
      collectMountTasks(val, path.concat(key), acc);
    }
  });
  return acc;
};

const mountComponents = (map: ComponentOrMapping) => {
  const tasks = collectMountTasks(map);
  bootLog('dynamic mounts collected', { count: tasks.length });

  // Reason: chunk the mounting work so boot stays responsive even if extensions freeze long tasks.
  void runInChunks(tasks, ({ selector, Component: Comp }) => {
    document.querySelectorAll(selector).forEach((el) => {
      bootLog('dynamic mount render start', { selector });
      const root = createRoot(el);
      bootLog('dynamic RouterProvider create start', { selector });
      const router = createBrowserRouter(
        [{ path: '*', element: <Comp /> }],
        {
          future: {
            v7_relativeSplatPath: true,
          },
        },
      );
      bootLog('dynamic RouterProvider create end', { selector });

      root.render(
        <ReduxProvider store={store}>
          <PersistGate
            loading={<BootLoadingScreen scope="dynamic-mount" />}
            persistor={persistor}
            onBeforeLift={() => {
              bootLog('dynamic PersistGate onBeforeLift start', { selector });
              // El bootstrap de sesión y carrito pertenece al root. Los
              // montajes dinámicos comparten el store y no deben competir
              // con esa comprobación.
              bootLog('dynamic PersistGate onBeforeLift end', { selector });
            }}
          >
            <Providers>
              {/* Mantengo BrowserRouter solo para estos mounts aislados */}
              {/* y con los mismos future flags que ya usabas */}
              {/* (no afecta al data router principal de la SPA). */}
              <RouterProvider
                future={{ v7_startTransition: true }}
                router={router}
              />
            </Providers>
          </PersistGate>
        </ReduxProvider>,
      );
      bootLog('dynamic mount render scheduled', { selector });
    });
  }, { chunkSize: 1, scheduler: scheduleIdle });
};

// -----------------------------------------------------------------------------
// 3) 📌 Data Router principal de la SPA
//    Definimos el router usando `createBrowserRouter` y como elemento raíz
//    usamos <GuardedApp />, que internamente usa `useMatches`.
// -----------------------------------------------------------------------------
bootLog('appRouter create start');
const appRouter = createBrowserRouter(
  [
    { path: '*', element: <GuardedApp /> },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  },
);
bootLog('appRouter create end');

const RootRouterProvider = () => {
  bootLog('root RouterProvider render');
  return <RouterProvider future={{ v7_startTransition: true }} router={appRouter} />;
};

// -----------------------------------------------------------------------------
// 4) 📌 SPA principal en #root (RouterProvider en lugar de BrowserRouter)
// -----------------------------------------------------------------------------
const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Elemento #root no encontrado');

bootLog('root render start');
createRoot(rootEl).render(
  <StrictMode>
    <ReduxProvider store={store}>
      <PersistGate
        loading={<BootLoadingScreen scope="root" />}
        persistor={persistor}
        onBeforeLift={() => {
          bootLog('root PersistGate onBeforeLift start');
          store.dispatch(checkSession({ isPublic: isBootPublicPath() }));
          store.dispatch(loadInitialCart());
          bootLog('root PersistGate onBeforeLift end');
        }}
      >
        {/* ConfigProvider y QueryClientProvider para theming y datos */}
        <Providers>
          {/* CAMBIO 3: Reemplazamos <BrowserRouter> por <RouterProvider>. */}
          <RootRouterProvider />
        </Providers>
      </PersistGate>
    </ReduxProvider>
  </StrictMode>,
);
bootLog('root render scheduled');

// -----------------------------------------------------------------------------
// 5) 📌 Montaje dinámico (se mantiene igual)
// -----------------------------------------------------------------------------
bootLog('dynamic mounts start');
mountComponents(componentMapping);
